// TASK-3 REWRITE: light validation table + stat cards + scopes figure + a
// `DroneHero3D` replay section (same realistic 3D replay as the Home hero) + the Isaac
// render figure. Replaces the dark-theme `Panel` chrome throughout.
import { Eyebrow } from "@/components/Eyebrow";
import { Card } from "@/components/Card";
import { Section, SectionHeading } from "@/components/kit";
import { DroneHero3D } from "@/components/DroneHero3D";
import { VALIDATION_ROWS, VALIDATION_TOTAL, TWIN_CARDS } from "@/content/twin";
import reconInProcess from "@/assets/captures/recon-inprocess.jpg";
// GPU render; the import path stays identical so this page needs no change.
import isaacRender from "@/assets/isaac-render.png";

function ValidationTable() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
      <div className="border-b border-border bg-surface px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
        RUN_ISAAC_E2E · NVIDIA DGX Spark · GB10
      </div>
      <table className="w-full font-mono text-sm">
        <tbody>
          {VALIDATION_ROWS.map((row) => (
            <tr key={row.k} className="odd:bg-surface">
              <td className="px-5 py-3 text-muted-foreground">{row.k}</td>
              <td className="px-5 py-3 text-right text-foreground">
                <span className="inline-flex items-center gap-2">
                  {row.v}
                  {"pass" in row && row.pass && (
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  )}
                </span>
              </td>
            </tr>
          ))}
          <tr className="bg-primary-tint">
            <td className="px-5 py-3 font-semibold text-foreground">{VALIDATION_TOTAL.k}</td>
            <td className="px-5 py-3 text-right font-semibold text-primary">
              <span className="inline-flex items-center justify-end gap-2">
                {VALIDATION_TOTAL.v}
                {VALIDATION_TOTAL.pass && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function DigitalTwin() {
  return (
    <>
      <Section wash="wash" bordered>
        <Eyebrow>Validation · gated end-to-end on the GPU</Eyebrow>
        <h1 className="mt-5 max-w-3xl text-balance text-5xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
          The physics is real — and we proved it.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
          Every merge to the airframe bridge runs a GPU-gated end-to-end suite against NVIDIA
          Isaac Sim — reachability, a level hover, and analytic agreement — before it ships.
        </p>
        <div className="mt-10 max-w-2xl">
          <ValidationTable />
        </div>
      </Section>

      <Section bordered>
        <div className="grid gap-6 sm:grid-cols-2">
          {TWIN_CARDS.map((card) => (
            <Card key={card.title}>
              <div className="font-mono text-3xl font-extrabold text-primary">{card.big}</div>
              <h3 className="mt-3 text-xl font-bold tracking-tight">{card.title}</h3>
              <p className="mt-2 leading-relaxed text-muted-foreground">{card.body}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section wash="surface" bordered>
        <Eyebrow>Under the hood</Eyebrow>
        <h2 className="mt-4 max-w-2xl text-balance text-4xl font-extrabold tracking-tight">
          One bridge, two physics engines.
        </h2>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">
          The airframe block speaks one small protocol to whatever is computing rigid-body
          physics underneath it —{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-foreground">ping</code> ·{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-foreground">connect</code> ·{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-foreground">setInputs</code> ·{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-foreground">step</code> ·{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-foreground">getOutputs</code>.
          Swap the fast analytic 6-DOF model for NVIDIA Isaac Sim's GPU rigid body and nothing
          upstream changes — every functional model, every wire on the canvas, every scope —
          because it's a change to one endpoint, not to the design.
        </p>
        <figure className="mt-10 overflow-hidden rounded-2xl border border-border bg-card shadow-lift">
          <img
            src={reconInProcess}
            alt="Live scopes in the Workshop reading airframe position and rate signals streamed back from NVIDIA Isaac Sim during a running co-simulation."
            loading="lazy"
            className="block w-full"
          />
          <figcaption className="border-t border-border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
            digital twin · scopes · live — airframe:pos_z ← Isaac
          </figcaption>
        </figure>
      </Section>

      <Section bordered>
        <SectionHeading
          align="left"
          eyebrow="Recorded flight"
          title="Replay the recorded flight."
          intro="A real take-off, hover and landing computed by the same functional models and NVIDIA Isaac Sim physics — drag to orbit, or let the camera do it."
        />
        <div className="mt-10 overflow-hidden rounded-2xl border border-border bg-card shadow-lift">
          <DroneHero3D />
        </div>
      </Section>

      <Section wash="surface">
        <SectionHeading
          align="left"
          eyebrow="Rendered on the GPU"
          title="The same scene, rendered."
          intro="Isaac Sim doesn't just compute the physics — it renders the scene those bodies live in, on the same GPU."
        />
        <figure className="mt-10 overflow-hidden rounded-2xl border border-border bg-card shadow-lift">
          <img
            src={isaacRender}
            alt="Isaac Sim RTX render of the drone airframe in the physics scene, on NVIDIA GB10."
            loading="lazy"
            className="block w-full"
          />
          <figcaption className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
            Rendered by Isaac Sim on NVIDIA GB10 — the same scene the physics runs in.
          </figcaption>
        </figure>
      </Section>
    </>
  );
}
